import React, {useMemo} from "react";
import {ColDef} from "ag-grid-community";
import {CustomCellRendererProps} from "ag-grid-react";
import {AdminUser} from "services/adminsService";
import {AvailableApp} from "services/loginServices";
import {Button} from "react-bootstrap";
import {AiOutlineClose, AiOutlineRedo} from "react-icons/ai";
import {AppNames} from "enums";
import {FaRegCopy, FaRegEdit} from "react-icons/fa";
import {appLinksSortValue, groupAppLinksByEnv,} from "pages/Admins/adminAppLinksUtils";
import {AdminSuperAdminFilter} from "pages/Admins/filters/AdminSuperAdminFilter";
import {AdminAppLinksFilter} from "pages/Admins/filters/AdminAppLinksFilter";
import toast from "react-hot-toast";
import {AdminRolesLabels, AdminRolesStyle} from "enums/AdminsRoles";

const appLinksGrouped = (links: AvailableApp[] | undefined) => {
    const byApp = groupAppLinksByEnv(links)
    if (!byApp.size) {
        return null
    }
    return (
        <div
            className="d-flex flex-column gap-1 py-1"
            style={{fontSize: 12, lineHeight: 1.35}}
        >
            {[...byApp.entries()]
                .sort(([a], [b]) => a - b)
                .map(([appId, env]) => (
                    <div
                        key={appId}
                        className="d-flex align-items-center flex-wrap"
                        style={{gap: "6px 8px"}}
                    >
                        <span className="fw-semibold text-body">{AppNames[appId]}</span>
                        <span className="d-inline-flex align-items-center flex-wrap" style={{gap: 4}}>
                            {env.prod && (
                                <span
                                    className="badge rounded-pill bg-black"
                                    title="Продакшен"
                                >
                                    Prod
                                </span>
                            )}
                            {env.test && (
                                <span
                                    className="badge rounded-pill bg-secondary"
                                    title="Тестовое окружение"
                                >
                                    Test
                                </span>
                            )}
                        </span>
                    </div>
                ))}
        </div>
    )
}

type AdminsTableHandlers = {
    onBlock: (key: string, name: string) => void
    onRegenerate: (key: string, name: string) => void
    onEditAccess: (admin: AdminUser) => void
    onCopyToken: (token: string) => void
}

const copyUserKey = async (userKey: string) => {
    try {
        await navigator.clipboard.writeText(userKey)
        toast.success("Ключ скопирован")
    } catch {
        toast.error("Не получилось скопировать ключ")
    }
}

const getColumnDefs = (handlers: AdminsTableHandlers): ColDef<AdminUser>[] => {
    const UserKeyCell = (params: CustomCellRendererProps<AdminUser>) => {
        const key = params.data?.userKey
        if (key == null) {
            return null
        }
        return (
            <span
                role="button"
                tabIndex={0}
                className="text-decoration-underline"
                title="Нажмите, чтобы скопировать ключ"
                style={{cursor: "pointer"}}
                onClick={(e) => {
                    e.stopPropagation()
                    void copyUserKey(key)
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        e.stopPropagation()
                        void copyUserKey(key)
                    }
                }}
            >
                {key}
            </span>
        )
    }

    const ActionsCell = (params: CustomCellRendererProps<AdminUser>) => {
        const a = params.data
        if (!a) {
            return null
        }
        return (
            <div className="d-flex flex-row flex-wrap align-items-start" style={{gap: 4}}>
                <Button
                    variant="outline-dark"
                    size="sm"
                    title="Пересоздать токен"
                    onClick={() => handlers.onRegenerate(a.userKey, a.name)}
                    className="d-flex align-items-center"
                >
                    <AiOutlineRedo size={18} className="mx-1"/> Токен
                </Button>
                <Button
                    variant="outline-primary"
                    size="sm"
                    title="Приложения, среды, суперадмин"
                    onClick={() => handlers.onEditAccess(a)}
                    className="d-flex align-items-center"
                >
                    <FaRegEdit size={16} className="mx-1"/> Редактировать
                </Button>
                <Button
                    variant="outline-danger"
                    title="Заблокировать"
                    size="sm"
                    onClick={() => handlers.onBlock(a.userKey, a.name)}
                    className="d-flex align-items-center"
                >
                    <AiOutlineClose size={18} className="mx-1"/> Заблокировать
                </Button>
                <Button
                    variant="outline-dark"
                    size="sm"
                    disabled={!a.token}
                    title={`Скопировать токен ${a.token}`}
                    onClick={() => handlers.onCopyToken(a.token)}
                    className="d-flex align-items-center"
                >
                    <FaRegCopy size={18} className="mx-1"/>
                    Токен
                </Button>
            </div>
        )
    }

    return [
        {
            headerName: "Ключ",
            field: "userKey",
            flex: 1.2,
            cellRenderer: UserKeyCell,
        },
        {
            headerName: "Имя",
            field: "name",
            flex: 1.2,
            filter: true,
        },
        {
            headerName: "Роль",
            field: "role",
            flex: 0.9,
            filter: AdminSuperAdminFilter,
            cellRenderer: (p: CustomCellRendererProps<AdminUser>) =>
                p.data?.role ? <span
                    className={`badge ${AdminRolesStyle[p.data.role]}`}>{AdminRolesLabels[p.data.role]}</span> : "-",
        },
        {
            headerName: "Приложения",
            flex: 1.3,
            filter: AdminAppLinksFilter,
            valueGetter: (p) => appLinksSortValue(p.data?.appLinks),
            cellRenderer: (p: CustomCellRendererProps<AdminUser>) => (
                <>{appLinksGrouped(p.data?.appLinks)}</>
            ),
            autoHeight: true,
            cellStyle: {lineHeight: 1.2, paddingTop: 4, paddingBottom: 4},
        },
        {
            headerName: "Просмотрено",
            field: "reviewedUserCount",
            flex: 1,
            filter: false,
            sortable: true
        },
        {
            headerName: "",
            colId: "actions",
            sortable: false,
            filter: false,
            flex: 2.8,
            cellRenderer: ActionsCell,
            wrapText: true,
            autoHeight: true,
            cellStyle: {paddingTop: 4, paddingBottom: 4},
        },
    ]
}

export const useAdminsTable = (handlers: AdminsTableHandlers) => {
    const columnDefs = useMemo(() => getColumnDefs(handlers), [handlers])

    return {columnDefs}
}
